import React, { useState, useMemo } from 'react';
import { SquareStack } from 'lucide-react';
import { Pen } from 'lucide-react';
import { CircleCheckBig } from 'lucide-react';
import { CircleX } from 'lucide-react';
import './style.css';
import DatapointChangeCard from '../DatapointChangeCard/DatapointChangeCard';
import DatapointHistory from '../DatapointHistory/DatapointHistory';

const TimelineView = ({ menuSections, datapointTimelines, projectTimelines, changeLog, datapoints, projects }) => {
    const [datapointHistoryItem, setDatapointHistoryItem] = useState(null);

    console.log('Current datapointHistoryItem:', datapointHistoryItem);  // Debug current state

    console.log('menuSections:', menuSections);

    console.log(datapointTimelines)
    console.log(projectTimelines)

    // Process all changes once
    const processedChanges = useMemo(() => {
        const allChanges = {};

        changeLog.forEach(entry => {
            const changes = [];

            // Process each individual project change
            entry.Completed_Projects.new.forEach(projectId => {
                const projectName = projects[projectId]?.strategy_name || projectId;
                changes.push({
                    type: 'project',
                    id: projectId,
                    name: projectName,
                    status: 'Marked as complete',
                    status_type: 'completed'
                });
            });

            entry.Completed_Projects.removed.forEach(projectId => {
                const projectName = projects[projectId]?.strategy_name || projectId;
                changes.push({
                    type: 'project',
                    id: projectId,
                    name: projectName,
                    status: 'Marked as incomplete',
                    status_type: 'incomplete'
                });
            });

            // Process each individual datapoint change
            Object.entries(entry.User_Inputs.new).forEach(([key, value]) => {
                const datapointName = datapoints.find(d => d.datapoint === key)?.headline || key;
                changes.push({
                    type: 'datapoint',
                    id: key,
                    name: datapointName,
                    status: `Changed to ${value}`,
                    status_type: 'changed'
                });
            });

            Object.keys(entry.User_Inputs.removed).forEach(key => {
                const datapointName = datapoints.find(d => d.datapoint === key)?.headline || key;
                changes.push({
                    type: 'datapoint',
                    id: key,
                    name: datapointName,
                    status: 'Removed',
                    status_type: 'removed'
                });
            });

            allChanges[entry.Timestamp] = {
                timestamp: entry.Timestamp,
                date: new Date(entry.Timestamp * 1000),
                changes
            };
        });

        return allChanges;
    }, [changeLog, datapoints, projects]);

    // Sort timestamps for timeline
    const sortedTimestamps = useMemo(() => {
        return Object.keys(processedChanges)
            .sort((a, b) => b - a)
            .map(timestamp => ({
                timestamp: parseInt(timestamp),
                date: processedChanges[timestamp].date
            }));
    }, [processedChanges]);

    // State for selected version
    const [selectedVersion, setSelectedVersion] = useState(sortedTimestamps[0]?.timestamp);

    // Group timestamps by date for the timeline
    const groupedByDate = useMemo(() => {
        const groups = {};
        sortedTimestamps.forEach(entry => {
            const dateStr = entry.date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
            });
            if (!groups[dateStr]) {
                groups[dateStr] = [];
            }
            groups[dateStr].push(entry);
        });
        return groups;
    }, [sortedTimestamps]);

    const selectedVersionDetails = processedChanges[selectedVersion];

    // Get timeline length for a specific change
    const getTimelineLength = (changeType, changeId) => {
        const timelines = changeType === 'datapoint' ? datapointTimelines : projectTimelines;
        const item = timelines.find(item =>
            changeType === 'datapoint'
                ? item.datapoint_name === changeId
                : item.project_id === changeId
        );
        return item?.timeline?.length || 0;
    };

    // Helper to determine change types for a specific timestamp
    const getChangeTypes = (timestamp) => {
        const changes = processedChanges[timestamp]?.changes || [];
        return {
            hasFirstChanges: changes.some(change => getTimelineLength(change.type, change.id) <= 1),
            hasSubsequentChanges: changes.some(change => getTimelineLength(change.type, change.id) > 1)
        };
    };

    // Process menuSections into a lookup structure
    const datapointLookup = useMemo(() => {
        if (!menuSections || !Array.isArray(menuSections)) {
            return {};
        }

        const lookup = {};

        menuSections.forEach(section => {
            if (!section.options) return;

            section.options.forEach(option => {
                if (!option.data) return;

                option.data.forEach(item => {
                    const isProject = section.id === 'resilience';
                    const id = isProject ? item.project_id : item.datapoint;
                    if (!id) return;

                    lookup[id] = {
                        type: isProject ? 'project' : 'datapoint',
                        headline: item.headline,
                        section_label: section.title,
                        sub_section_label: option.label,
                        icons: {
                            dark: section.icon_dark,
                            light: section.icon_light
                        }
                    };
                });
            });
        });

        return lookup;
    }, [menuSections]);

    const handleExploreClick = (id) => {
        const itemInfo = datapointLookup[id];
        if (!itemInfo) return;

        // Get timeline based on type
        const timeline = itemInfo.type === 'project'
            ? projectTimelines.find(t => t.project_id === id)
            : datapointTimelines.find(t => t.datapoint_name === id);

        const historyItem = {
            icons: itemInfo.icons,
            section_label: itemInfo.section_label,
            sub_section_label: itemInfo.sub_section_label,
            timeline: timeline,
            data_type: itemInfo.type === 'datapoint'
                ? (datapoints.find(dp => dp.datapoint === id)?.data_type || null)
                : null,
            headline: itemInfo.headline
        };

        setDatapointHistoryItem(historyItem);
    };

    console.log(datapointHistoryItem)
    return (
        <div className="version-history-container">
            {/* Left panel - Version Details */}
            <div className="version-details">
                {datapointHistoryItem ? (
                    <DatapointHistory
                        historyItem={datapointHistoryItem}
                        returnTo="Timeline View"
                        returnToFunction={() => setDatapointHistoryItem(null)}
                    />
                ) : (
                    <>
                        <h2>Version history</h2>
                        {selectedVersionDetails && (
                            <div className="version-content">
                                <div className="version-header">
                                    <h3>{selectedVersionDetails.date.toLocaleDateString('en-US', {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}</h3>
                                    <p>{new Intl.DateTimeFormat('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        timeZoneName: 'short'
                                    }).format(selectedVersionDetails.date)}</p>
                                </div>
                                <div className="timeline-changes-list">
                                    {selectedVersionDetails.changes.map((change, index) => (
                                        <DatapointChangeCard
                                            key={index}
                                            change={change}
                                            getTimelineLength={getTimelineLength}
                                            onExploreClick={() => handleExploreClick(change.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                    </>
                )}
            </div>
            {/* Right panel - Timeline */}
            <div className="version-timeline">
                {Object.entries(groupedByDate).map(([dateStr, entries]) => (
                    <div key={dateStr} className="date-group">
                        <div className="date-header">{dateStr}</div>
                        {entries.map(entry => {
                            const changeTypes = getChangeTypes(entry.timestamp);
                            return (
                                <div
                                    key={entry.timestamp}
                                    className={`timeline-entry ${entry.timestamp === selectedVersion ? 'selected' : ''}`}
                                    onClick={() => setSelectedVersion(entry.timestamp)}
                                >
                                    <div className="entry-time">
                                        {entry.date.toLocaleTimeString('en-US', {
                                            hour: 'numeric',
                                            minute: '2-digit'
                                        })}
                                    </div>
                                    <div className="change-indicators">
                                        {changeTypes.hasFirstChanges && <div className="indicator first-change" />}
                                        {changeTypes.hasSubsequentChanges && <div className="indicator subsequent-change" />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TimelineView;
